/**
 * Backbone view which provides editor functionality for a 'file' field type.
 * ## Fields
 * The `value` field gets and sets the current value, which is the current Document ID.
 * ## Events
 * The `'change'` event is raised whenever the value changes. The event has a single argument, the new value.
 * @module app/Field/FileEditor
 */
import BaseFieldView from "core/views/Field/BaseFieldView";
import _ from "underscore";
import $ from "jquery";
export default BaseFieldView.extend({
  template: _.template(
    '<input class="form-control" <%- accept ? ("accept=\\"" + accept + "\\"") : "" %> type="file" name="file" /><% if(url){%><a target="_blank" href="<%-url%>">Download</a><a href="#" class="delete-file">Delete</a><%}%>'
  ),

  tagName: "form",
  attributes: {
    enctype: "multipart/form-data",
  },

  /**
   * Constructor for this class.
   * @param  {object} options The hash of options.
   * @param {string} [options.accept] If specified, provides an "accept" attribute on the file tag.
   * @param {string} [options.fileOptions] A hash of attributes which should be set on any new Document models created by this instance.
   * @param {string} options.value The current field value.
   * @param {Object} options.documentFactory An object which contains a member function "createDocument" which takes the attributes of the document and a Context as arguments and returns a document instance to use. The returned object should be an instance of Backbone.Model.
   * @param {string} [options.url] The URL to use in preference to that supplied by the document object.
   * @param {Function} [options.urlFactory] A function which, given a document, returns the URL to use. Called with the context of this control.
   */
  initialize: function (options) {
    BaseFieldView.prototype.initialize.apply(this, arguments);

    this.options = _.defaults({}, options, {
      accept: false,
      fileOptions: {},
    });
  },

  startLoad: function () {
    var deferred = new $.Deferred();
    var options = this.options;

    if (this.value) {
      var doc = options.documentFactory.createDocument({
        id: this.value,
      });

      doc.fetch({
        success: function () {
          deferred.resolve(
            _.extend({}, options, {
              document: doc,
            })
          );
        },
        error: function (model, response) {
          if (response.status == 404) {
            console.error(
              "Could not find the file, but do not delete file. Possible to still change the file"
            );
            deferred.resolve(options);
          } else {
            deferred.reject(response);
          }
        },
      });
    } else {
      deferred.resolve(options);
    }

    return deferred.promise();
  },

  events: {
    "click .delete-file": "deleteFile",
    "change input": function () {
      this.updateFile(); //don't want to have library code do something weird with the return value
    },
  },

  updateFile: function (value) {
    var self = this;

    if (value) {
      this.$el.ajaxSubmit({
        url: self.options.documentFactory.createDocumentFileUrl(value),
        type: "POST",
        success: function () {
          self.setValue(value);
          self.onChange();
        },
      });
    } else {
      var doc = self.options.documentFactory.createDocument(
        self.options.fileOptions
      );

      doc.save(null, {
        success: function () {
          self.updateFile(doc.id);
        },
        error: function (model, response) {
          self.renderError(response);
        },
      });
    }
  },

  deleteFile: function (evt) {
    var self = this;
    var doc = this.options.documentFactory.createDocument({
      id: this.value,
    });

    doc.destroy({
      success: function () {
        self.value = null;
        self.onChange();
      },
      error: function (model, response) {
        self.renderError(response);
      },
    });

    evt.preventDefault();
  },

  serialize: function (items) {
    var url = "";

    if (
      items.document &&
      !items.document.isNew() &&
      items.document.get("Name")
    ) {
      url =
        _.result(items.document, "url") + "/file/" + items.document.get("Name");
    }

    if (_.isString(items.url)) {
      url = items.url;
    }

    if (_.isFunction(items.urlFactory)) {
      url = items.urlFactory.call(this, items.document);
    }

    return {
      accept: items.accept,
      url: url,
    };
  },

  getControlValue: function () {
    return this.$el.data("value");
  },

  setControlValue: function (value) {
    this.$el.data("value", value);
    this.render();
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/core/views/Field/edit/FileEditor.js
// module id = 841
// module chunks = 1